import * as Sentry from '@sentry/react'
import { kUserFilesFileNameMaxLength, kUserFilesValidatedUploadMaxFileSizeBytes, kUserFilesValidatedUploadMaxPageCount } from '@/user_files/limits'
import { hasInvalidUserFilesNameCharacter, kInvalidUserFilesNameMessage } from '@/user_files/utils/name-validation'
import { formatFileSize } from './drive-list-format'

const kBytesPerValidatedUploadMegabyte = 1_000_000

const kLegacyOfficeExtensionReplacements: Readonly<Record<string, string>> = {
  doc: 'docx',
  ppt: 'pptx',
  xls: 'xlsx',
}

function formatBackendMegabytes(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
}

/** Returns actionable conversion guidance for filename types rejected by the upload picker. */
export function getUserFilesUploadFilenameGuidance(filename: string): string | null {
  const cleaned = filename.trim().replace(/[. \t]+$/u, '')
  const finalDot = cleaned.lastIndexOf('.')
  if (finalDot < 0) return null

  const extension = cleaned
    .slice(finalDot + 1)
    .trim()
    .toLowerCase()
  const replacement = kLegacyOfficeExtensionReplacements[extension]
  return replacement ? `Legacy .${extension} files are not supported; convert to .${replacement}.` : null
}

export function buildFileTooLargeError(sizeBytes: number): string {
  const observedMegabytes = Math.ceil((sizeBytes / kBytesPerValidatedUploadMegabyte) * 10) / 10
  const limitLabel = formatFileSize(kUserFilesValidatedUploadMaxFileSizeBytes, 0)
  return `File size ${formatBackendMegabytes(observedMegabytes)} MB exceeds the ${limitLabel} limit.`
}

function isUserFilesPdfFile(file: File | Blob): boolean {
  if (file instanceof File) {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  }

  return file.type === 'application/pdf'
}

/**
 * Mirrors the file pipeline validated-upload PDF page limit.
 * Lazy loads PDF.js only when called to avoid increasing bundle size.
 */
async function validateUserFilesPdfPageCount(file: File | Blob): Promise<{ error: boolean; message: string }> {
  try {
    const pdfjs = await import('pdfjs-dist')

    if (!pdfjs.GlobalWorkerOptions.workerSrc) {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString()
    }

    const arrayBuffer = await file.arrayBuffer()
    const loadingTask = pdfjs.getDocument({
      data: arrayBuffer,
      // TODO(ENG-7148): Remove once pdfjs-dist is upgraded to >= 4.2.67,
      // where the vulnerable glyph-eval path was removed.
      isEvalSupported: false,
    })
    const pdf = await loadingTask.promise

    if (pdf.numPages > kUserFilesValidatedUploadMaxPageCount) {
      return {
        error: true,
        message: `File has ${pdf.numPages} pages, exceeding the ${kUserFilesValidatedUploadMaxPageCount}-page limit.`,
      }
    }
  } catch (error) {
    console.error('Error reading PDF:', error)
    Sentry.captureException(error)
    return {
      error: true,
      message: 'Unable to read PDF file',
    }
  }

  return { error: false, message: '' }
}

export async function validateUserFilesUploadFile(file: File): Promise<string | null> {
  if (file.name.length > kUserFilesFileNameMaxLength) {
    return `File name must be ${kUserFilesFileNameMaxLength} characters or less.`
  }

  if (hasInvalidUserFilesNameCharacter(file.name)) {
    return kInvalidUserFilesNameMessage
  }

  const filenameGuidance = getUserFilesUploadFilenameGuidance(file.name)
  if (filenameGuidance) {
    return filenameGuidance
  }

  if (file.size > kUserFilesValidatedUploadMaxFileSizeBytes) {
    return buildFileTooLargeError(file.size)
  }

  if (isUserFilesPdfFile(file)) {
    const validationError = await validateUserFilesPdfPageCount(file)
    if (validationError.error) {
      return validationError.message
    }
  }

  return null
}
